import { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const ProtectedRoute = ({ children, requiredRole }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const retryCountRef = useRef(0);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (location.state?.user || hasCheckedRef.current) return;
    
    const checkAuth = async () => {
      hasCheckedRef.current = true;
      try {
        const response = await axios.get('/api/auth/me', {
          withCredentials: true,
          timeout: 15000
        });
        setUser(response.data);
        setIsAuthenticated(true);
        // Update localStorage with fresh data
        localStorage.setItem('vgws_user', JSON.stringify({
          ...response.data,
          session_created: Date.now(),
          session_expires: Date.now() + (365 * 24 * 60 * 60 * 1000)
        }));
      } catch (error) {
        // Retry once on network error
        if ((error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response) && retryCountRef.current < 1) {
          retryCountRef.current += 1;
          hasCheckedRef.current = false;
          return;
        }
        
        // Fallback: Check localStorage for cached session
        const cached = localStorage.getItem('vgws_user');
        if (cached) {
          try {
            const cachedUser = JSON.parse(cached);
            if (cachedUser.session_expires && cachedUser.session_expires > Date.now()) {
              setUser(cachedUser);
              setIsAuthenticated(true);
              return;
            }
          } catch (err) {
            // Cached user data corrupt — fall through to re-auth
            if (process.env.NODE_ENV !== 'production') {
              console.debug('ProtectedRoute: cached user parse failed', err?.message);
            }
          }
        }
        
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs sm:text-sm text-slate-500">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check role requirements
  if (requiredRole && user?.role !== requiredRole) {
    if (user?.is_caredesk_owner && user?.access_roles?.includes(requiredRole)) {
      return children;
    }
    if (user?.is_registered_staff && user?.can_switch_interface) {
      if (requiredRole === 'ticket_creator' || requiredRole === 'process_owner') {
        return children;
      }
    }
    if (user?.role === 'cao') {
      return <Navigate to="/cao" replace />;
    } else if (user?.role === 'process_owner') {
      return <Navigate to="/process-owner" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
